import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { API_URL, BULAN, formatApiError, formatRupiah, getToken, SPP_TAHUN , formatTanggal} from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_BENDAHARA } from "@/lib/nav";
import { NAV_KEPSEK } from "@/lib/nav";
import BackupExcelButton from "@/components/BackupExcelButton";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Plus, Minus, Download, X, FileText, Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BULAN_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const BULAN_KEYS = ["01","02","03","04","05","06","07","08","09","10","11","12"];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));

function TabunganExcelControls({ onReload }) {
  const [busy, setBusy] = useState(false);
  const downloadTemplate = () => {
    const t = getToken();
    const url = `${API_URL}/excel/tabungan/template${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
    window.open(url, "_blank");
  };
  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/excel/tabungan/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil import: ${data.inserted} baris setoran`);
      if (data.errors?.length) toast.error(`Error (${data.errors.length}): ${data.errors.slice(0, 3).join(" | ")}`);
      onReload?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <button onClick={downloadTemplate} className="pp-btn-secondary" data-testid="tab-dl-template">
        <FileSpreadsheet className="w-4 h-4" /> Template Excel
      </button>
      <label className="pp-btn-secondary cursor-pointer" data-testid="tab-upload-label">
        <Upload className="w-4 h-4" /> {busy ? "Mengimport…" : "Upload Setoran (Massal)"}
        <input type="file" accept=".xlsx" onChange={handleUpload} className="hidden" data-testid="tab-upload-input" />
      </label>
    </>
  );
}

// -------------- RINCIAN TABUNGAN --------------
export function RincianTabungan({ nav = NAV_BENDAHARA, canWrite = true }) {
  const navigate = useNavigate();
  const DEFAULT_BULAN = String(new Date().getMonth() + 1).padStart(2, "0");
  const [tahun, setTahun] = useState(SPP_TAHUN[0]);
  const [dataType, setDataType] = useState("seluruh");
  const [status, setStatus] = useState("aktif");
  const [bulan, setBulan] = useState(DEFAULT_BULAN);
  const [siswaId, setSiswaId] = useState("");
  const [applied, setApplied] = useState({ tahun: SPP_TAHUN[0], dataType: "seluruh", status: "aktif", bulan: DEFAULT_BULAN, siswaId: "" });
  const [siswaList, setSiswaList] = useState([]);
  const [payload, setPayload] = useState(null); // {view, data}
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/siswa", { params: { status } });
      setSiswaList(data);
    })();
  }, [status]);

  const load = useCallback(async () => {
    setLoading(true); setPayload(null);
    try {
      if (applied.dataType === "seluruh") {
        const { data } = await api.get("/tabungan/rincian/seluruh-siswa", { params: { tahun: applied.tahun, bulan: applied.bulan, status: applied.status } });
        setPayload({ view: "seluruh", data });
      } else if (applied.dataType === "siswa") {
        if (!applied.siswaId) { setLoading(false); return; }
        const { data } = await api.get("/tabungan/rincian/siswa-tertentu", { params: { tahun: applied.tahun, siswa_id: applied.siswaId } });
        setPayload({ view: "siswa", data });
      } else if (applied.dataType === "rekap-perhari") {
        const { data } = await api.get("/tabungan/rincian/rekap-perhari", { params: { tahun: applied.tahun } });
        setPayload({ view: "rekap-perhari", data });
      } else if (applied.dataType === "rekap-tahunan") {
        const { data } = await api.get("/tabungan/rincian/rekap-tahunan", { params: { tahun: applied.tahun } });
        setPayload({ view: "rekap-tahunan", data });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); }
  }, [applied]);

  useEffect(() => { load(); }, [load]);

  const applyFilter = () => setApplied({ tahun, dataType, status, bulan, siswaId });
  const resetFilter = () => {
    setTahun(SPP_TAHUN[0]); setDataType("seluruh"); setStatus("aktif"); setBulan(DEFAULT_BULAN); setSiswaId("");
    setApplied({ tahun: SPP_TAHUN[0], dataType: "seluruh", status: "aktif", bulan: DEFAULT_BULAN, siswaId: "" });
  };

  const downloadHref = () => {
    const t = getToken();
    const params = new URLSearchParams({ tahun: applied.tahun, view: applied.dataType });
    if (applied.dataType === "seluruh") { params.append("bulan", applied.bulan); params.append("status", applied.status); }
    if (applied.dataType === "siswa") { if (!applied.siswaId) return null; params.append("siswa_id", applied.siswaId); }
    if (t) params.append("auth", t);
    return `${API_URL}/tabungan/download/rincian?${params.toString()}`;
  };

  return (
    <Layout nav={nav}>
      <PageTitle title="Rincian Tabungan" action={
        <div className="flex gap-2">
          <BackupExcelButton endpoint="tabungan" filters={{
            tahun: applied.tahun,
            status: applied.status,
            bulan: applied.dataType === "seluruh" ? applied.bulan : "",
            siswa_id: applied.dataType === "siswa" ? applied.siswaId : "",
          }} testId="backup-tabungan-btn" />
          {downloadHref() && (
            <a href={downloadHref()} target="_blank" rel="noreferrer" className="pp-btn-secondary" data-testid="tab-rincian-download">
              <Download className="w-4 h-4" /> Download
            </a>
          )}
          {canWrite && (
            <>
              <TabunganExcelControls onReload={load} />
              <button onClick={() => navigate("/bendahara/tabungan/input")} className="pp-btn-primary" data-testid="tab-input-setoran">
                <Plus className="w-4 h-4" /> Input Tabungan
              </button>
              <button onClick={() => navigate("/bendahara/tabungan/penarikan")} className="pp-btn-secondary" data-testid="tab-input-penarikan">
                <Minus className="w-4 h-4" /> Input Penarikan
              </button>
            </>
          )}
        </div>
      } />

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="pp-label">Tahun</label>
            <select className="pp-input" value={tahun} onChange={(e) => setTahun(parseInt(e.target.value, 10))} data-testid="tab-tahun">
              {SPP_TAHUN.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="pp-label">Data</label>
            <select className="pp-input" value={dataType} onChange={(e) => setDataType(e.target.value)} data-testid="tab-data-type">
              <option value="seluruh">Seluruh Siswa</option>
              <option value="siswa">Siswa Tertentu</option>
              <option value="rekap-perhari">Rekap Perhari</option>
              <option value="rekap-tahunan">Rekap Tahunan</option>
            </select>
          </div>
          {(dataType === "seluruh" || dataType === "siswa") && (
            <div>
              <label className="pp-label">Status</label>
              <select className="pp-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="tab-status">
                <option value="aktif">Siswa Aktif</option>
                <option value="tidak_aktif">Siswa Tidak Aktif</option>
              </select>
            </div>
          )}
          {dataType === "seluruh" && (
            <div>
              <label className="pp-label">Bulan</label>
              <select className="pp-input" value={bulan} onChange={(e) => setBulan(e.target.value)} data-testid="tab-bulan">
                {BULAN.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
              </select>
            </div>
          )}
          {dataType === "siswa" && (
            <div>
              <label className="pp-label">Nama Siswa</label>
              <select className="pp-input" value={siswaId} onChange={(e) => setSiswaId(e.target.value)} data-testid="tab-siswa">
                <option value="">— Pilih Siswa —</option>
                {siswaList.map((s) => <option key={s.id} value={s.id}>{s.nama}{s.nis ? ` — ${s.nis}` : ""}</option>)}
              </select>
            </div>
          )}
          <div className="flex items-end gap-2">
            <button onClick={applyFilter} className="pp-btn-primary flex-1" data-testid="tab-apply">Terapkan</button>
            <button onClick={resetFilter} className="pp-btn-secondary flex-1" data-testid="tab-reset">Reset</button>
          </div>
        </div>
      </section>

      {loading && <div className="pp-card p-12 text-center text-slate-400 italic">Memuat…</div>}
      {!loading && payload?.view === "seluruh" && <SeluruhTable data={payload.data} />}
      {!loading && payload?.view === "siswa" && <SiswaTable data={payload.data} />}
      {!loading && payload?.view === "rekap-perhari" && <RekapPerhariTable data={payload.data} />}
      {!loading && payload?.view === "rekap-tahunan" && <RekapTahunanTable data={payload.data} />}
    </Layout>
  );
}

function SeluruhTable({ data }) {
  return (
    <section className="pp-card pp-freeze overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-[1400px] w-full text-xs" data-testid="tab-seluruh-table">
          <thead>
            <tr className="pp-table-header">
              <th className="text-left px-3 py-2 sticky left-0 bg-indigo-600 z-10 min-w-[180px] text-white">Nama</th>
              {DAYS.map((d) => <th key={d} className="text-right px-1.5 py-2">{parseInt(d, 10)}</th>)}
              <th className="text-right px-3 py-2">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={33} className="text-center py-10 text-slate-400 italic">Tidak ada aktivitas.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.siswa_id} className="pp-row">
                <td className="px-3 py-2 sticky left-0 bg-white z-10 font-medium">{r.nama}</td>
                {DAYS.map((d) => {
                  const v = r.per_hari[d];
                  return <td key={d} className="px-1.5 py-2 text-right font-mono">{v > 0 ? formatRupiah(v).replace("Rp ", "") : "—"}</td>;
                })}
                <td className="px-3 py-2 text-right font-mono font-semibold">{formatRupiah(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SiswaTable({ data }) {
  return (
    <section className="pp-card pp-freeze overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-[900px] w-full text-xs" data-testid="tab-siswa-table">
          <thead><tr className="pp-table-header">
            <th className="text-left px-3 py-2">Tgl</th>
            {BULAN_SHORT.map((b) => <th key={b} className="text-right px-2 py-2">{b}</th>)}
            <th className="text-right px-3 py-2">TOTAL</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.tanggal} className="pp-row">
                <td className="px-3 py-2 font-medium">{r.tanggal}</td>
                {BULAN_KEYS.map((b) => <td key={b} className="px-2 py-2 text-right font-mono">{r.per_bulan[b] > 0 ? formatRupiah(r.per_bulan[b]).replace("Rp ", "") : "—"}</td>)}
                <td className="px-3 py-2 text-right font-mono font-semibold">{r.total > 0 ? formatRupiah(r.total) : "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-indigo-50 font-bold">
              <td className="px-3 py-2">TOTAL</td>
              {BULAN_KEYS.map((b) => <td key={b} className="px-2 py-2 text-right font-mono">{data.col_totals[b] > 0 ? formatRupiah(data.col_totals[b]).replace("Rp ", "") : "—"}</td>)}
              <td className="px-3 py-2 text-right font-mono">{formatRupiah(data.grand_total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function RekapPerhariTable({ data }) { return <SiswaTable data={data} />; }

function RekapTahunanTable({ data }) {
  return (
    <section className="pp-card pp-freeze overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-[1000px] w-full text-xs" data-testid="tab-rekap-tahunan-table">
          <thead><tr className="pp-table-header">
            <th className="text-left px-3 py-2 sticky left-0 bg-indigo-600 z-10 text-white">No</th>
            <th className="text-left px-3 py-2 sticky left-8 bg-indigo-600 z-10 min-w-[180px] text-white">Nama</th>
            {BULAN_SHORT.map((b) => <th key={b} className="text-right px-2 py-2">{b}</th>)}
            <th className="text-right px-3 py-2">JUMLAH TABUNGAN</th>
          </tr></thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={15} className="text-center py-10 text-slate-400 italic">Belum ada siswa yang menabung tahun ini.</td></tr>}
            {data.rows.map((r, idx) => (
              <tr key={r.siswa_id} className="pp-row">
                <td className="px-3 py-2 sticky left-0 bg-white z-10 text-slate-500">{idx + 1}</td>
                <td className="px-3 py-2 sticky left-8 bg-white z-10 font-medium">{r.nama}</td>
                {BULAN_KEYS.map((b) => <td key={b} className="px-2 py-2 text-right font-mono">{r.per_bulan[b] > 0 ? formatRupiah(r.per_bulan[b]).replace("Rp ", "") : "—"}</td>)}
                <td className="px-3 py-2 text-right font-mono font-semibold">{formatRupiah(r.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-indigo-50 font-bold">
              <td className="px-3 py-2 sticky left-0 bg-indigo-50 z-10"></td>
              <td className="px-3 py-2 sticky left-8 bg-indigo-50 z-10">TOTAL</td>
              {BULAN_KEYS.map((b) => <td key={b} className="px-2 py-2 text-right font-mono">{data.col_totals[b] > 0 ? formatRupiah(data.col_totals[b]).replace("Rp ", "") : "—"}</td>)}
              <td className="px-3 py-2 text-right font-mono">{formatRupiah(data.grand_total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

// -------------- INPUT SETORAN --------------
export function InputTabungan() {
  const navigate = useNavigate();
  const [siswaList, setSiswaList] = useState([]);
  const [form, setForm] = useState({
    siswa_id: "", tanggal: new Date().toISOString().slice(0, 10), jumlah: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/siswa", { params: { status: "aktif" } });
      setSiswaList(data);
    })();
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.siswa_id) return toast.error("Pilih siswa");
    if (parseFloat(form.jumlah) <= 0) return toast.error("Jumlah harus > 0");
    setSaving(true);
    try {
      await api.post("/tabungan/setoran", {
        siswa_id: form.siswa_id, tanggal: form.tanggal, jumlah: parseFloat(form.jumlah),
      });
      toast.success("Tabungan tersimpan");
      navigate("/bendahara/tabungan/rincian");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };
  return (
    <Layout nav={NAV_BENDAHARA}>
      <PageTitle title="Input Tabungan" />
      <section className="pp-card p-6 max-w-lg">
        <form onSubmit={submit} className="grid gap-4" data-testid="tab-setoran-form">
          <div><label className="pp-label">Nama Siswa *</label>
            <SearchableSelect
              options={siswaList.map((s) => ({ value: s.id, label: `${s.nama}${s.nis ? ` — ${s.nis}` : ""}` }))}
              value={form.siswa_id}
              onChange={(v) => setForm({ ...form, siswa_id: v })}
              placeholder="— Ketik nama siswa untuk mencari —"
              testId="tab-setoran-siswa"
            />
          </div>
          <div><label className="pp-label">Tanggal *</label>
            <input required type="date" className="pp-input" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} data-testid="tab-setoran-tgl" />
          </div>
          <div><label className="pp-label">Jumlah (Rp) *</label>
            <input required type="number" min={1} className="pp-input" value={form.jumlah} onChange={(e) => setForm({ ...form, jumlah: e.target.value })} data-testid="tab-setoran-jumlah" />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button type="button" onClick={() => navigate("/bendahara/tabungan/rincian")} className="pp-btn-secondary">Batal</button>
            <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="tab-setoran-submit">
              {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </form>
      </section>
    </Layout>
  );
}

// -------------- INPUT PENARIKAN --------------
export function InputPenarikan() {
  const navigate = useNavigate();
  const [siswaList, setSiswaList] = useState([]);
  const [status, setStatus] = useState("aktif");
  const [form, setForm] = useState({
    siswa_id: "", tanggal: new Date().toISOString().slice(0, 10), jumlah: "", keterangan: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/siswa", { params: { status } });
      setSiswaList(data);
    })();
  }, [status]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.siswa_id) return toast.error("Pilih siswa");
    if (parseFloat(form.jumlah) <= 0) return toast.error("Jumlah harus > 0");
    setSaving(true);
    try {
      await api.post("/tabungan/penarikan", {
        siswa_id: form.siswa_id, tanggal: form.tanggal,
        jumlah: parseFloat(form.jumlah), keterangan: form.keterangan,
      });
      toast.success("Penarikan tabungan tersimpan");
      navigate("/bendahara/tabungan/saldo");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };
  return (
    <Layout nav={NAV_BENDAHARA}>
      <PageTitle title="Input Penarikan Tabungan" />
      <section className="pp-card p-6 max-w-lg">
        <form onSubmit={submit} className="grid gap-4" data-testid="tab-penarikan-form">
          <div><label className="pp-label">Status Siswa *</label>
            <select className="pp-input" value={status} onChange={(e) => { setStatus(e.target.value); setForm({ ...form, siswa_id: "" }); }} data-testid="tab-penarikan-status">
              <option value="aktif">Siswa Aktif</option>
              <option value="tidak_aktif">Siswa Tidak Aktif</option>
            </select>
          </div>
          <div><label className="pp-label">Nama Siswa *</label>
            <SearchableSelect
              options={siswaList.map((s) => ({ value: s.id, label: `${s.nama}${s.nis ? ` — ${s.nis}` : ""}` }))}
              value={form.siswa_id}
              onChange={(v) => setForm({ ...form, siswa_id: v })}
              placeholder="— Ketik nama siswa untuk mencari —"
              testId="tab-penarikan-siswa"
            />
          </div>
          <div><label className="pp-label">Jumlah Pengambilan (Rp) *</label>
            <input required type="number" min={1} className="pp-input" value={form.jumlah} onChange={(e) => setForm({ ...form, jumlah: e.target.value })} data-testid="tab-penarikan-jumlah" />
          </div>
          <div><label className="pp-label">Tanggal Pengambilan *</label>
            <input required type="date" className="pp-input" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} data-testid="tab-penarikan-tgl" />
          </div>
          <div><label className="pp-label">Keterangan</label>
            <textarea rows={2} className="pp-input" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} data-testid="tab-penarikan-ket" />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button type="button" onClick={() => navigate("/bendahara/tabungan/saldo")} className="pp-btn-secondary">Batal</button>
            <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="tab-penarikan-submit">
              {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </form>
      </section>
    </Layout>
  );
}

// -------------- SALDO TABUNGAN --------------
export function SaldoTabungan({ nav = NAV_BENDAHARA, canWrite = true }) {
  const navigate = useNavigate();
  const CUR_YEAR = new Date().getFullYear();
  const DEFAULT_TAHUN = SPP_TAHUN.includes(CUR_YEAR) ? CUR_YEAR : SPP_TAHUN[0];
  const [status, setStatus] = useState("seluruh");
  const [siswaList, setSiswaList] = useState([]);
  const [siswaId, setSiswaId] = useState("");
  const [tahun, setTahun] = useState(DEFAULT_TAHUN);
  const [applied, setApplied] = useState({ status: "seluruh", siswaId: "", tahun: DEFAULT_TAHUN });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rekap, setRekap] = useState(null);

  const isSeluruh = applied.status === "seluruh";

  useEffect(() => {
    if (status === "seluruh") { setSiswaList([]); return; }
    (async () => {
      const { data } = await api.get("/siswa", { params: { status } });
      setSiswaList(data);
    })();
  }, [status]);

  useEffect(() => {
    // Load rekap when in "seluruh" view
    if (!isSeluruh) { setRekap(null); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tabungan/saldo/rekap", { params: { tahun: applied.tahun } });
        setRekap(data);
      } catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat"); }
      finally { setLoading(false); }
    })();
  }, [isSeluruh, applied.tahun]);

  useEffect(() => {
    if (!applied.siswaId || isSeluruh) { setData(null); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tabungan/saldo", { params: { siswa_id: applied.siswaId } });
        setData(data);
      } catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat"); }
      finally { setLoading(false); }
    })();
  }, [applied.siswaId, isSeluruh]);

  const applyFilter = () => setApplied({ status, siswaId: status === "seluruh" ? "" : siswaId, tahun });
  const resetFilter = () => { setStatus("seluruh"); setSiswaId(""); setTahun(DEFAULT_TAHUN); setApplied({ status: "seluruh", siswaId: "", tahun: DEFAULT_TAHUN }); };

  const downloadHref = () => {
    if (!applied.siswaId || isSeluruh) return null;
    const t = getToken();
    return `${API_URL}/tabungan/download/saldo/${applied.siswaId}${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
  };

  return (
    <Layout nav={nav}>
      <PageTitle title="Saldo Tabungan" action={
        <div className="flex gap-2">
          {downloadHref() && (
            <a href={downloadHref()} target="_blank" rel="noreferrer" className="pp-btn-secondary" data-testid="tab-saldo-download">
              <Download className="w-4 h-4" /> Download
            </a>
          )}
          {canWrite && (
            <>
              <button onClick={() => navigate("/bendahara/tabungan/input")} className="pp-btn-primary" data-testid="tab-input-setoran">
                <Plus className="w-4 h-4" /> Input Tabungan
              </button>
              <button onClick={() => navigate("/bendahara/tabungan/penarikan")} className="pp-btn-secondary" data-testid="tab-input-penarikan">
                <Minus className="w-4 h-4" /> Input Penarikan
              </button>
            </>
          )}
        </div>
      } />

      {/* Grand total di atas — hanya saat mode "Seluruh Siswa" */}
      {isSeluruh && rekap && (
        <section className="pp-card p-5 mb-4 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white" data-testid="tab-saldo-grand-total">
          <div className="text-[11px] uppercase tracking-wider text-white/70 mb-3" data-testid="grand-tahun-label">Rekap Tabungan Tahun {rekap.tahun || applied.tahun}</div>
          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/80">Jumlah Siswa</div>
              <div className="font-bold text-2xl mt-1" data-testid="grand-jumlah-siswa">{rekap.jumlah_siswa}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/80">Total Setoran</div>
              <div className="font-bold text-2xl mt-1">{formatRupiah(rekap.grand_total_setoran)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/80">Total Penarikan</div>
              <div className="font-bold text-2xl mt-1">{formatRupiah(rekap.grand_total_penarikan)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/80">Saldo Real (Total)</div>
              <div className="font-bold text-3xl mt-1" data-testid="grand-saldo">{formatRupiah(rekap.grand_total_saldo)}</div>
            </div>
          </div>
        </section>
      )}

      <section className="pp-card p-5 mb-6">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="pp-label">Status Siswa</label>
            <select className="pp-input" value={status} onChange={(e) => { setStatus(e.target.value); setSiswaId(""); }} data-testid="tab-saldo-status">
              <option value="seluruh">Seluruh Siswa (Rekap)</option>
              <option value="aktif">Siswa Aktif</option>
              <option value="tidak_aktif">Siswa Tidak Aktif</option>
            </select>
          </div>
          {status === "seluruh" && (
            <div>
              <label className="pp-label">Tahun</label>
              <select className="pp-input" value={tahun} onChange={(e) => setTahun(parseInt(e.target.value, 10))} data-testid="tab-saldo-tahun">
                {SPP_TAHUN.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
          {status !== "seluruh" && (
            <div>
              <label className="pp-label">Nama Siswa</label>
              <select className="pp-input" value={siswaId} onChange={(e) => setSiswaId(e.target.value)} data-testid="tab-saldo-siswa">
                <option value="">— Pilih Siswa —</option>
                {siswaList.map((s) => <option key={s.id} value={s.id}>{s.nama}{s.nis ? ` — ${s.nis}` : ""}</option>)}
              </select>
            </div>
          )}
          <div className="flex items-end gap-2">
            <button onClick={applyFilter} className="pp-btn-primary flex-1" data-testid="tab-saldo-apply">Terapkan</button>
            <button onClick={resetFilter} className="pp-btn-secondary flex-1" data-testid="tab-saldo-reset">Reset</button>
          </div>
        </div>
      </section>

      {loading && <div className="pp-card p-12 text-center text-slate-400 italic">Memuat…</div>}

      {/* Rekap seluruh siswa */}
      {!loading && isSeluruh && rekap && (
        <section className="pp-card pp-freeze overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tab-saldo-rekap-table">
              <thead><tr className="pp-table-header">
                <th className="text-left px-4 py-3">Nama Siswa</th>
                <th className="text-left px-4 py-3">NISN</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Total Setoran</th>
                <th className="text-right px-4 py-3">Total Penarikan</th>
                <th className="text-right px-4 py-3">Saldo Sisa</th>
                <th className="text-center px-4 py-3">Aksi</th>
              </tr></thead>
              <tbody>
                {rekap.rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic">Belum ada data.</td></tr>}
                {rekap.rows.map((r) => (
                  <tr key={r.siswa_id} className="pp-row" data-testid={`rekap-row-${r.siswa_id}`}>
                    <td className="px-4 py-3 font-medium">{r.nama}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.nis || "—"}</td>
                    <td className="px-4 py-3"><span className={r.status === "aktif" ? "pp-badge-success" : "pp-badge-neutral"}>{r.status === "aktif" ? "Aktif" : "Tidak Aktif"}</span></td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700">{formatRupiah(r.total_setoran)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-rose-700">{formatRupiah(r.total_penarikan)}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-indigo-700">{formatRupiah(r.saldo)}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => { setStatus(r.status); setSiswaId(r.siswa_id); setApplied({ status: r.status, siswaId: r.siswa_id, tahun: applied.tahun }); }} className="pp-btn-ghost text-xs" data-testid={`rekap-open-${r.siswa_id}`}>Detail</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-indigo-50 font-bold" data-testid="rekap-grand-total-row">
                  <td className="px-4 py-3" colSpan={3}>TOTAL ({rekap.jumlah_siswa} siswa)</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700" data-testid="rekap-total-setoran">{formatRupiah(rekap.grand_total_setoran)}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-rose-700" data-testid="rekap-total-penarikan">{formatRupiah(rekap.grand_total_penarikan)}</td>
                  <td className="px-4 py-3 text-right font-mono text-indigo-700" data-testid="rekap-total-saldo">{formatRupiah(rekap.grand_total_saldo)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}

      {!loading && !isSeluruh && !applied.siswaId && <div className="pp-card p-12 text-center text-slate-400 italic">Pilih siswa lalu klik Terapkan untuk melihat saldo.</div>}
      {!loading && !isSeluruh && data && (
        <section className="pp-card pp-freeze overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-xs uppercase text-slate-500">Nama Siswa</div>
              <div className="font-semibold text-lg">{data.siswa.nama} <span className="text-xs text-slate-400 font-mono">({data.siswa.nis || "—"})</span></div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase text-slate-500">Saldo Akhir</div>
              <div className="font-bold text-2xl text-indigo-700">{formatRupiah(data.saldo_akhir)}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tab-saldo-table">
              <thead><tr className="pp-table-header">
                <th className="text-left px-4 py-3">Tahun</th>
                <th className="text-right px-4 py-3">Setoran</th>
                <th className="text-left px-4 py-3">Tgl Penarikan</th>
                <th className="text-right px-4 py-3">Jml Penarikan</th>
                <th className="text-left px-4 py-3">Keterangan</th>
                <th className="text-right px-4 py-3">Debit</th>
                <th className="text-right px-4 py-3">Kredit</th>
                <th className="text-right px-4 py-3">Saldo</th>
                <th className="text-left px-4 py-3">Bukti</th>
              </tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-400 italic">Belum ada aktivitas tabungan.</td></tr>}
                {data.rows.map((r) => (
                  <tr key={r.tahun} className="pp-row">
                    <td className="px-4 py-3 font-medium">{r.tahun}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{r.setoran ? formatRupiah(r.setoran) : "—"}</td>
                    <td className="px-4 py-3 text-xs whitespace-pre-line">{r.penarikan.length ? r.penarikan.map((p) => p.tanggal).join("\n") : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs whitespace-pre-line">{r.penarikan.length ? r.penarikan.map((p) => formatRupiah(p.jumlah)).join("\n") : "—"}</td>
                    <td className="px-4 py-3 text-xs whitespace-pre-line">{r.penarikan.length ? r.penarikan.map((p) => p.keterangan || "—").join("\n") : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700">{r.debit ? formatRupiah(r.debit) : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-rose-700">{r.kredit ? formatRupiah(r.kredit) : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{formatRupiah(r.saldo)}</td>
                    <td className="px-4 py-3">
                      {r.penarikan.length === 0 ? "—" : (
                        <div className="flex flex-col gap-1">
                          {r.penarikan.map((p) => {
                            const t = getToken();
                            const href = `${API_URL}/tabungan/penarikan/${p.id}/kwitansi.pdf${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
                            return (
                              <a key={p.id} href={href} target="_blank" rel="noreferrer" className="text-red-600 inline-flex items-center gap-1 text-xs" data-testid={`tab-kwitansi-${p.id}`}>
                                <FileText className="w-3 h-3" /> Kwitansi
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </Layout>
  );
}

export function RincianTabunganKepsek() { return <RincianTabungan nav={NAV_KEPSEK} canWrite={false} />; }
export function SaldoTabunganKepsek() { return <SaldoTabungan nav={NAV_KEPSEK} canWrite={false} />; }
